import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, CheckCheck } from "lucide-react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;  // 10s — feels "live" without hammering the API

/** Play a short two-tone "buzzer" alert through the Web Audio API. We avoid
 *  shipping an audio asset so first paint stays fast and we don't depend on
 *  network for the sound to fire. Falls back silently if the browser blocks
 *  it (e.g. before any user gesture). */
function playAlertSound() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    // Chrome/Safari sometimes start the context in "suspended" state until
    // a gesture explicitly resumes it. Trying resume() is harmless and
    // unlocks the buzzer when the user has already clicked elsewhere
    // (e.g. the login button) earlier in the session.
    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    const fire = () => {
      const now = ctx.currentTime;
      const beep = (start, freq, duration = 0.18, gainPeak = 0.20) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(gainPeak, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(start);
        osc.stop(start + duration + 0.02);
      };
      // Two-tone alert: high → low, like a soft buzzer
      beep(now,        880);
      beep(now + 0.22, 660);
      setTimeout(() => { try { ctx.close(); } catch {} }, 700);
    };

    if (ctx.state === "running") fire();
    else setTimeout(fire, 60);  // wait briefly for resume() to take effect
  } catch {
    // Browser blocked autoplay — silently ignore
  }
}

/**
 * Notifications bell — owns the global polling loop for the current user's
 * notifications. Renders an unread count dot, a quick dropdown of recent
 * items, and (critically) fires the `open-triage-modal` window event for
 * support_user when a brand-new `ticket_created` notification arrives while
 * the user is logged in. The TicketsPage listens for that event and pops
 * the triage modal automatically.
 */
export default function NotificationsBell({ to }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  // Track ids we've already seen so we only auto-open the triage modal
  // for *new* notifications, not on every poll.
  const seenIdsRef = useRef(null);          // null = first load (no auto-open)
  const dropdownRef = useRef(null);

  const isSupport = user?.role === "support_user" || user?.role === "admin";

  const triggerTriage = useCallback(async (notif) => {
    const ticketId = notif?.payload?.ticket_id;
    if (!ticketId) return;
    playAlertSound();
    const dispatch = () => window.dispatchEvent(
      new CustomEvent("open-triage-modal", { detail: { ticket_id: ticketId } }),
    );
    if (!window.location.pathname.endsWith("/tickets")) {
      navigate("/users/tickets");
      // Give TicketsPage time to mount and register its listener
      setTimeout(dispatch, 500);
    } else {
      dispatch();
    }
    // Mark this notification as read so the bell decrements
    try { await axios.post(`${API}/api/notifications/${notif.id}/read`, {}, { withCredentials: true }); } catch {}
  }, [navigate]);

  const fetchNotifs = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/notifications/me?limit=20`,
        { withCredentials: true },
      );
      const list = Array.isArray(data) ? data : [];
      setItems(list);

      const isFirstLoad = seenIdsRef.current === null;
      if (isFirstLoad) seenIdsRef.current = new Set();

      if (user?.role === "support_user") {
        const unreadTickets = list.filter((n) => n.kind === "ticket_created" && !n.read);

        if (isFirstLoad) {
          // Just logged in — surface the most-recent unread ticket as a
          // triage modal so the support user doesn't miss anything.
          if (unreadTickets.length > 0) {
            const newest = unreadTickets[0];
            unreadTickets.forEach((n) => seenIdsRef.current.add(n.id));
            await triggerTriage(newest);
          }
        } else {
          // Subsequent polls — fire for brand-new ticket_created notifications
          const freshTickets = unreadTickets.filter((n) => !seenIdsRef.current.has(n.id));
          if (freshTickets.length > 0) {
            freshTickets.forEach((n) => seenIdsRef.current.add(n.id));
            await triggerTriage(freshTickets[0]);
          }
          // Also play sound for new ticket_reply notifications (no auto-modal)
          const freshReplies = list.filter(
            (n) => n.kind === "ticket_reply" && !n.read && !seenIdsRef.current.has(n.id),
          );
          if (freshReplies.length > 0 && freshTickets.length === 0) {
            playAlertSound();
            freshReplies.forEach((n) => seenIdsRef.current.add(n.id));
          }
        }
      } else if (!isFirstLoad) {
        // IT admin / college_admin / faculty — play sound for any fresh unread
        // notification (e.g. a support reply to their ticket).
        const freshAny = list.filter((n) => !n.read && !seenIdsRef.current.has(n.id));
        if (freshAny.length > 0) {
          playAlertSound();
          freshAny.forEach((n) => seenIdsRef.current.add(n.id));
        }
      }

      // Backfill seenIds with everything currently visible so we never
      // re-fire for the same notification twice.
      list.forEach((n) => seenIdsRef.current.add(n.id));
    } catch (e) {
      // Silent — bell stays where it was
    }
  }, [user?.role, triggerTriage]);

  useEffect(() => {
    if (!user) return undefined;
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(id);
  }, [user, fetchNotifs]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const unreadCount = items.filter((n) => !n.read).length;

  const handleClickItem = async (n) => {
    setOpen(false);
    // Mark read
    if (!n.read) {
      try { await axios.post(`${API}/api/notifications/${n.id}/read`, {}, { withCredentials: true }); } catch {}
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    // For any ticket notification, navigate to tickets and open the ticket detail.
    // Super Admin uses /tickets; portal roles use /users/tickets.
    if (
      (n.kind === "ticket_created" || n.kind === "ticket_reply" || n.kind === "ticket_updated") &&
      n.payload?.ticket_id
    ) {
      const target = user?.role === "admin" ? "/tickets" : "/users/tickets";
      const dispatch = () => window.dispatchEvent(
        new CustomEvent("open-triage-modal", { detail: { ticket_id: n.payload.ticket_id } })
      );
      if (window.location.pathname !== target) {
        navigate(target);
        setTimeout(dispatch, 300);
      } else {
        dispatch();
      }
    } else if (to) {
      navigate(to);
    }
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/api/notifications/read-all`, {}, { withCredentials: true });
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch {}
  };

  return (
    <div className="notif-bell-wrap" ref={dropdownRef}>
      <button
        type="button"
        className="topnav-bell"
        title="Notifications"
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
      >
        <Bell size={17} />
        {unreadCount > 0 && (
          <span className="topnav-bell-dot" data-testid="notifications-bell-dot">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown" role="menu" data-testid="notif-dropdown">
          <div className="notif-dropdown-head">
            <span className="notif-dropdown-title">Notifications</span>
            {unreadCount > 0 && (
              <button
                className="notif-mark-all"
                onClick={handleMarkAllRead}
                data-testid="notif-mark-all"
              >
                <CheckCheck size={13} /> Mark all read
              </button>
            )}
            <button
              className="notif-dropdown-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="notif-dropdown-list">
            {items.length === 0 ? (
              <div className="notif-empty">You're all caught up.</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  className={`notif-item${n.read ? "" : " notif-item-unread"}`}
                  onClick={() => handleClickItem(n)}
                  data-testid={`notif-item-${n.id}`}
                >
                  <span className="notif-item-title">{n.title || n.kind}</span>
                  {n.body && <span className="notif-item-body">{n.body}</span>}
                  {n.payload?.college_name && (
                    <span className="notif-item-meta">{n.payload.college_name}</span>
                  )}
                  {n.payload?.author_name && (
                    <span className="notif-item-meta">{n.payload.author_name}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
